<template>
    <BaseDialog
        v-model:open="open"
        title="Development goal"
        :show-close="!isSavingTargetDate"
        :disable-close-on-backdrop-click="isSavingTargetDate"
        float-close
        dialog-content-class="rounded-[10px]! p-5 sm:p-[30px] md:!max-w-[500px]">
        <div ref="dialogElement" data-testid="development-goal-details-dialog" class="flex flex-col gap-[15px]">
            <header class="flex flex-col">
                <p class="flex items-center gap-1.5 pr-8">
                    <span class="size-2 shrink-0 rounded-full bg-[#81DADA]" aria-hidden="true"></span>
                    <span class="scaffold-details-small text-valence-stone-500">Started {{ formatStartedOn(goal.created_at) }}</span>
                </p>
                <h2 class="body font-medium text-valence-stone-900">{{ goal.title }}</h2>
            </header>

            <hr class="h-px border-0 bg-valence-cream-350" />

            <div class="flex flex-col gap-2.5">
                <section class="flex flex-col">
                    <h3 class="scaffold-body-small-medium text-valence-stone-900">What success looks like</h3>
                    <p class="details-big text-valence-stone-500">{{ goal.achievement_criteria }}</p>
                </section>

                <section class="flex flex-col self-start">
                    <BaseNotification v-if="targetDateError" class="mb-1" theme="error" :text="targetDateError" hide-dismiss />
                    <h3 v-if="goal.target_date" class="scaffold-body-small-medium text-valence-stone-900">Target date</h3>
                    <div class="details-big flex items-center gap-1.5 text-valence-stone-500">
                        <time v-if="goal.target_date" :datetime="goal.target_date">{{ formatTargetDate(goal.target_date) }}</time>
                        <BasePopover
                            v-model:open="isCalendarOpen"
                            as-child
                            side="bottom"
                            align="start"
                            :side-offset="6"
                            content-class="rounded-[10px]! border-valence-cream-300! p-0 shadow-[0_18px_48px_rgba(0,0,0,0.18)]!">
                            <template #trigger>
                                <BaseButton
                                    v-if="goal.target_date"
                                    type="button"
                                    theme="tertiary"
                                    size="sm"
                                    icon-only
                                    class="text-valence-cream-400 hover:bg-transparent hover:text-valence-stone-900"
                                    :aria-label="`Change target date (currently ${formatTargetDate(goal.target_date)})`"
                                    :disabled="isSavingTargetDate"
                                    data-testid="edit-development-goal-target-date">
                                    <Pencil />
                                </BaseButton>
                                <BaseButton
                                    v-else
                                    type="button"
                                    theme="neutral"
                                    size="sm"
                                    class="base-btn-square inline-flex w-fit items-center gap-2.5 border-valence-cream-350 px-4 py-[9px] text-valence-stone-900 hover:bg-valence-cream-50"
                                    aria-label="Set target date"
                                    :disabled="isSavingTargetDate"
                                    data-testid="edit-development-goal-target-date">
                                    <Calendar class="size-4 shrink-0" foreground-class="stroke-current" />
                                    <span>Set target date</span>
                                </BaseButton>
                            </template>
                            <template #content>
                                <BaseCalendar
                                    :model-value="selectedTargetDate"
                                    :min-value="minimumTargetDate"
                                    :disabled="isSavingTargetDate"
                                    class="border-0!"
                                    @update:model-value="handleTargetDateSelected" />
                            </template>
                        </BasePopover>
                    </div>
                </section>
            </div>

            <hr class="h-px border-0 bg-valence-cream-350" />

            <div>
                <BaseNotification v-if="deleteError" class="mb-2.5" theme="error" :text="deleteError" hide-dismiss />
                <p data-testid="delete-development-goal-announcement" class="sr-only" aria-live="polite" aria-atomic="true">
                    {{ isConfirmingDeletion ? "Delete this goal? This can't be undone. Confirm deletion to continue." : "" }}
                </p>
                <div
                    v-if="isConfirmingDeletion"
                    class="flex flex-col items-start gap-2.5 rounded-[5px] bg-valence-cream-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-2.5 sm:py-[5px]"
                    data-testid="delete-development-goal-warning">
                    <p class="scaffold-details-small text-valence-stone-500 sm:whitespace-nowrap">
                        <span class="text-valence-stone-900">Delete this goal?</span>
                        This can't be undone
                    </p>
                    <div class="flex w-full shrink-0 items-center gap-2.5 sm:w-auto sm:justify-end">
                        <BaseButton
                            type="button"
                            theme="neutral"
                            size="sm"
                            class="base-btn-square min-h-11 flex-1 border-valence-cream-350 text-valence-stone-900 hover:bg-valence-cream-100 sm:min-h-0 sm:flex-none"
                            :disabled="isDeleting"
                            data-testid="cancel-delete-development-goal"
                            @click="void cancelDeletion()">
                            Cancel
                        </BaseButton>
                        <BaseButton
                            type="button"
                            theme="primary"
                            size="sm"
                            class="base-btn-brick min-h-11 flex-1 sm:min-h-0 sm:flex-none"
                            :disabled="isDeleting"
                            data-testid="confirm-delete-development-goal"
                            @click="deleteGoal">
                            {{ isDeleting ? "Deleting…" : "Delete" }}
                        </BaseButton>
                    </div>
                </div>
                <button
                    v-else
                    type="button"
                    class="body-small-semibold w-full cursor-pointer py-[11px] pl-[5px] text-left text-valence-brick-600 transition-colors hover:text-valence-brick-700"
                    data-testid="delete-development-goal"
                    @click="void startDeletionConfirmation()">
                    Delete goal
                </button>
            </div>
        </div>
    </BaseDialog>
</template>

<script setup lang="ts">
import { type DateValue, parseDate, today } from "@internationalized/date";
import { fetchApi } from "~vue/api";
import type { DevelopmentGoal, UpdateDevelopmentGoalRequest } from "~vue/api/generated/development-journeys";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCalendar from "~vue/components/BaseCalendar.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePopover from "~vue/components/BasePopover.vue";
import Calendar from "~vue/icons/Calendar.vue";
import Pencil from "~vue/icons/Pencil.vue";
import { DateTime } from "luxon";
import { computed, nextTick, ref, watch } from "vue";

const { goal, timezone } = defineProps<{
    goal: DevelopmentGoal;
    timezone?: string;
}>();

const open = defineModel<boolean>("open", { default: false });
const emit = defineEmits<{ deleted: [goalId: string]; updated: [goal: DevelopmentGoal] }>();
const isConfirmingDeletion = ref(false);
const isDeleting = ref(false);
const deleteError = ref<string | null>(null);
const isSavingTargetDate = ref(false);
const targetDateError = ref<string | null>(null);
const isCalendarOpen = ref(false);
const dialogElement = ref<HTMLElement | null>(null);

const minimumTargetDate = computed(() => today(timezone ?? "UTC"));

const selectedTargetDate = computed(() => (goal.target_date ? parseDate(goal.target_date) : undefined));

watch(open, (isOpen) => {
    if (!isOpen) {
        isConfirmingDeletion.value = false;
        deleteError.value = null;
        targetDateError.value = null;
        isCalendarOpen.value = false;
    }
});

const handleTargetDateSelected = (value: DateValue | undefined) => {
    if (!value) {
        return;
    }

    const selected = value.toString();
    if (selected === goal.target_date) {
        return;
    }

    void saveTargetDate(selected);
};

const saveTargetDate = async (targetDate: string) => {
    if (isSavingTargetDate.value) {
        return;
    }

    isSavingTargetDate.value = true;
    targetDateError.value = null;
    try {
        const body = { target_date: targetDate } satisfies UpdateDevelopmentGoalRequest;
        const response = await fetchApi(`/v1/development-journeys/goals/${goal.id}`, { method: "PATCH", body: JSON.stringify(body) });
        if (!response.ok) {
            throw new Error("Unable to update the development goal target date");
        }
        const updatedGoal = (await response.json()) as DevelopmentGoal;
        emit("updated", updatedGoal);
        isCalendarOpen.value = false;
    } catch {
        targetDateError.value = "We couldn't update this target date. Please try again.";
    } finally {
        isSavingTargetDate.value = false;
    }
};

const focusDialogControl = async (testId: string) => {
    await nextTick();
    dialogElement.value?.querySelector<HTMLElement>(`[data-testid="${testId}"]`)?.focus();
};

const startDeletionConfirmation = async () => {
    isConfirmingDeletion.value = true;
    await focusDialogControl("cancel-delete-development-goal");
};

const cancelDeletion = async () => {
    isConfirmingDeletion.value = false;
    deleteError.value = null;
    await focusDialogControl("delete-development-goal");
};

const deleteGoal = async () => {
    if (isDeleting.value) {
        return;
    }

    isDeleting.value = true;
    deleteError.value = null;
    try {
        const response = await fetchApi(`/v1/development-journeys/goals/${goal.id}`, { method: "DELETE" });
        if (!response.ok && response.status !== 404) {
            throw new Error("Unable to delete development goal");
        }
        open.value = false;
        emit("deleted", goal.id);
    } catch {
        deleteError.value = "We couldn't delete this goal. Please try again.";
    } finally {
        isDeleting.value = false;
    }
};

const formatTargetDate = (targetDate: string) => {
    const date = DateTime.fromISO(targetDate, { zone: "utc" });
    return date.isValid ? date.toFormat("LLLL d, yyyy") : targetDate;
};

const formatStartedOn = (createdAt: string) => {
    const date = DateTime.fromISO(createdAt, { zone: timezone ?? "utc" });
    return date.isValid ? date.toFormat("LLLL d") : createdAt;
};
</script>
